import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateFaq from "./UpdateFaq";
import { config } from "../../../../../config";

const apiUrl = config.apiUrl;

const ViewFaq = ({ onFaqUpdated }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    question: "",
    answer: ""
  });

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${apiUrl}/faqs`);
      setFaqs(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleEdit = (faq) => {
    setEditingFaq(faq.id);
    setFormData({
      slug: faq.slug,
      question: faq.question,
      answer: faq.answer
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(`${apiUrl}/faqsupdate/${editingFaq}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (onFaqUpdated) {
        onFaqUpdated();
      }
      await fetchFaqs();
      setEditingFaq(null);
      alert("FAQ updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the FAQ.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    
    try {
      await axios.delete(`${apiUrl}/faqdelete/${id}`);
      setFaqs(faqs.filter(faq => faq.id !== id));
      alert("FAQ deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete FAQ");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading FAQs...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All FAQs</h2>
      </div>

      {faqs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No FAQs found. Create your first FAQ.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {faqs.map((faq) => (
                <tr key={faq.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{faq.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{faq.question}</div>
                    <div className="text-sm text-gray-500 mt-1">{faq.answer}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(faq)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingFaq && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateFaq
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingFaq(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewFaq;